import { AppEntitlementValue, expandAppEntitlementView } from 'lib/actions/apps/app-entitlement'
import { AppUserValue, expandAppUserView } from 'lib/actions/apps/app-users'
import { expandTaskView, TaskValue } from 'lib/actions/task'
import { expandUserView, UserValue } from 'lib/actions/user'
import { expandValue } from 'lib/rpcts'
import {
    AccessReviewAppNavRollupRef,
    AccessReviewAppUserDetailRow,
    AccessReviewAppUserDetailRowSearchRef,
    AccessReviewRollup,
    AccessReviewSearchServiceUserRollupRef,
    AccessReviewSearchServiceUserRollupUserNavDataResponse,
    AccessReviewUserNavRollupRef,
    AccessReviewUserRollup,
    AppRollup,
    AppUserRollup,
} from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { AccessReviewSearchServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_search.client'
import { App } from 'pbts/c1/api/app/v1/app'
import { TaskCountRefreshRef } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { createUnaryApi } from 'redux_toolkit/lib/createUnaryApi'

export const UnknownIdentityUserId = 'unknown_account_owner_id'

export interface AccessReviewUserRollupValue {
    id: string
    user: UserValue | undefined
    openTaskCount: number
    completedTasksCount: number
    pendingActionsCount?: number
    pendingSubmissionCount?: number
}

interface AccessReviewUserRollupNavValue {
    userId: string
    currentUser: AccessReviewUserRollupValue | undefined
    previousUser: AccessReviewUserRollupValue | undefined
    nextUser: AccessReviewUserRollupValue | undefined
}

const transformAccessReviewUserRollupValueRow = (resp): Array<AccessReviewUserRollupValue> =>
    resp.list.map(
        (v: AccessReviewUserRollup): AccessReviewUserRollupValue => ({
            id: v.user?.user?.id || UnknownIdentityUserId,
            user: v.user ? expandUserView(v.user, resp.expanded) : undefined,
            openTaskCount: v.openTaskCount,
            completedTasksCount: v.completedTasksCount,
            pendingActionsCount: v.pendingActionsCount,
            pendingSubmissionCount: v.pendingSubmissionCount,
        }),
    )

const transformAccessReviewUserRollupNavValueRow = (
    resp: AccessReviewSearchServiceUserRollupUserNavDataResponse,
): AccessReviewUserRollupNavValue => {
    return {
        userId: resp.currentUser?.user?.user?.id || '',
        currentUser: resp.currentUser && {
            id: resp.currentUser?.user?.user?.id || UnknownIdentityUserId,
            user: expandUserView(resp.currentUser?.user, resp.currentUser?.expanded),
            openTaskCount: resp.currentUser?.openTaskCount,
            completedTasksCount: resp.currentUser?.completedTasksCount,
        },
        previousUser: resp.previousUser && {
            id: resp.previousUser?.user?.user?.id || UnknownIdentityUserId,
            user: expandUserView(resp.previousUser?.user, resp.previousUser?.expanded),
            openTaskCount: resp.previousUser?.openTaskCount,
            completedTasksCount: resp.previousUser?.completedTasksCount,
        },
        nextUser: resp.nextUser && {
            id: resp.nextUser?.user?.user?.id || UnknownIdentityUserId,
            user: expandUserView(resp.nextUser?.user, resp.nextUser?.expanded),
            openTaskCount: resp.nextUser?.openTaskCount,
            completedTasksCount: resp.nextUser?.completedTasksCount,
        },
    }
}

export const accessReviewUserRollupApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'userRollup',
    transformAccessReviewUserRollupValueRow,
    AccessReviewSearchServiceUserRollupRef.typeName,
)

export const accountsWithNoIdentityTaskCountApi = createUnaryApi(
    AccessReviewSearchServiceClient,
    'accountsWithNoIdentityTaskStatus',
    undefined,
    TaskCountRefreshRef.typeName,
)

export const accessReviewUserRollupNavDataApi = createUnaryApi(
    AccessReviewSearchServiceClient,
    'userRollupNavData',
    transformAccessReviewUserRollupNavValueRow,
    AccessReviewUserNavRollupRef.typeName,
)

export interface AccessReviewRollupValue extends AccessReviewRollup {
    id: string
}

const transformAccessReviewRollupValueRow = (resp): Array<AccessReviewRollupValue> =>
    resp.list.map((v): AccessReviewRollupValue => ({ id: v.accessReview?.id || '', ...v }))

export const accessReviewRollupApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'rollup',
    transformAccessReviewRollupValueRow,
)

type AppEntitlementValueWithTaskValue = {
    appEntitlement?: AppEntitlementValue
    task?: TaskValue
}

export interface AccessReviewAppUserDetailRowValue extends AccessReviewAppUserDetailRow {
    id: string
    appUserValue?: AppUserValue
    appEntitlementValues?: AppEntitlementValueWithTaskValue[]
    app?: App
    lastUsed?: Timestamp
}

const transformAccessReviewUserDetailRowRollupValue = (resp): Array<AccessReviewAppUserDetailRowValue> =>
    resp.list.map((v: AccessReviewAppUserDetailRow): AccessReviewAppUserDetailRowValue => {
        return {
            id: v.appUser?.appUser?.id || '',
            app: expandValue<App>(v.appUser?.appPath || '', App, resp.expanded),
            appEntitlementValues: v.entitlements?.map((appEntitlement) => {
                return {
                    appEntitlement: expandAppEntitlementView(appEntitlement.entitlement, resp.expanded),
                    task: expandTaskView(appEntitlement.task, resp.expanded),
                }
            }),
            appUserValue: expandAppUserView(v.appUser, resp.expanded),
            ...v,
        }
    })

export const accessReviewAppUserDetailRollupApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'appUserDetailRowRollup',
    transformAccessReviewUserDetailRowRollupValue,
    AccessReviewAppUserDetailRowSearchRef.typeName,
)

export const navNextSelectionApi = createUnaryApi(
    AccessReviewSearchServiceClient,
    'navNextSelection',
    undefined,
    TaskCountRefreshRef.typeName,
)

export interface AppUserRollupValue extends AppUserRollup {
    identityUser?: User
    app?: App
}

export type AppRollupValue = AppRollup

const transformSearchAppUserRollup = ({ list, expanded }): Array<AppUserRollupValue> => (
    list.map((v: AppUserRollup): AppUserRollupValue => ({
        ...v,
        identityUser: expandValue<User>(v.appUser?.identityUserPath || '', User, expanded),
        app: expandValue<App>(v?.appUser?.appPath || '', App, expanded),
    }))
)

export const searchAppUserRollupApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'searchAppUserRollup',
    transformSearchAppUserRollup,
    AccessReviewAppUserDetailRowSearchRef.typeName,
)

const transformSearchAppRollup = ({ list }): Array<AppRollupValue> => (
    list.map((v: AppRollup): AppRollupValue => ({
        ...v,
    }))
)

export const searchAppRollupApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'searchAppRollup',
    transformSearchAppRollup,
    AccessReviewAppNavRollupRef.typeName,
)
